/**
 * The decrypted view of a workspace.
 *
 * Sits beside `nodes.ts` and does the same job for workspaces that it does for
 * the personal tree: `api.ts` moves ciphertext, `crypto/` holds keys, and the
 * screens above work with readable names and message text.
 *
 * The one idea to hold on to: **a workspace key arrives the way a share does,
 * not the way a root does.** A personal root's key is wrapped under the master
 * key, so `keyForNode` can always walk down to it. A workspace root has no
 * single owner to wrap it under, so each member gets an ECIES envelope on their
 * membership row instead. Unwrapping that and seeding it into the same key cache
 * `nodes.ts` uses is all it takes to make every existing file operation —
 * listing, upload, download, move, rename — work inside a workspace unchanged.
 */

import {
  api,
  type MessageWire,
  type WorkspaceDetailWire,
  type WorkspaceMemberWire,
  type WorkspaceRole,
  type WorkspaceSummaryWire
} from './api';
import { requireVault } from './crypto/vault';
import { unwrapKeyFromSender, wrapKeyForRecipient } from './crypto/share';
import {
  chatKey,
  decryptMessage,
  encryptMessage,
  encodeMentions,
  namedChannelChatKey,
  type ChannelKind,
  projectChatKey
} from './crypto/workspace';
import { assertValidName, decryptName, encryptName } from './crypto/metadata';
import { generateNodeKey } from './crypto/keys';
import { cacheNodeKey, setWorkspaceKeyResolver } from './nodes';

export interface DecryptedWorkspace {
  id: string;
  rootNodeId: string;
  name: string;
  role: WorkspaceRole;
  isOwner: boolean;
  memberCount: number;
  unread: number;
  storage: { usedBytes: number };
  createdAt: number;
  /** The name would not decrypt. Rendered greyed rather than hidden — the
   *  workspace still exists and the user needs to be able to act on it. */
  undecryptable?: boolean;
}

export interface DecryptedMessage {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  mentions: string[];
  createdAt: number;
  editedAt: number | null;
  undecryptable?: boolean;
}

// ── the key cache ───────────────────────────────────────────────────────────

/**
 * Workspace id → the root node's key.
 *
 * Separate from `nodes.ts`'s cache, which is keyed on node id, because the seam
 * below needs to answer "do I have this *workspace*" before it knows the root's
 * node id. Both are dropped together when the vault locks.
 */
const workspaceKeys = new Map<string, CryptoKey>();
/** In-flight fetches, so twenty rows rendering at once cause one request and
 *  not twenty. */
const pending = new Map<string, Promise<CryptoKey>>();

export function forgetWorkspaceKeys(): void {
  workspaceKeys.clear();
  pending.clear();
}

/** Unwrap a member envelope and remember it under both the workspace id and the
 *  root node id, so `keyForNode` finds it by the latter. */
async function adoptKey(
  workspaceId: string,
  rootNodeId: string,
  wrapped: string
): Promise<CryptoKey> {
  const vault = requireVault();
  const key = await unwrapKeyFromSender(vault.privateKey, wrapped);
  workspaceKeys.set(workspaceId, key);
  cacheNodeKey(rootNodeId, key);
  return key;
}

/**
 * Make sure this workspace's root key is in the cache.
 *
 * Called by `nodes.ts` before it walks a chain whose root belongs to a
 * workspace — including on a cold reload straight onto a deep folder URL, where
 * nothing has been through the workspace list yet.
 */
export async function ensureWorkspaceKey(workspaceId: string): Promise<CryptoKey> {
  const cached = workspaceKeys.get(workspaceId);
  if (cached) return cached;

  const inFlight = pending.get(workspaceId);
  if (inFlight) return inFlight;

  const request = (async () => {
    const detail = await api.workspaces.get(workspaceId);
    if (!detail.nodeKeyWrapped) {
      throw new Error('This workspace has not been shared with you yet.');
    }
    return adoptKey(workspaceId, detail.rootNodeId, detail.nodeKeyWrapped);
  })();

  pending.set(workspaceId, request);
  try {
    return await request;
  } finally {
    pending.delete(workspaceId);
  }
}

export const cachedWorkspaceKey = (workspaceId: string): CryptoKey | null =>
  workspaceKeys.get(workspaceId) ?? null;

/**
 * Teach `nodes.ts` how to open a workspace root.
 *
 * At module scope so importing this file anywhere is enough — `state.tsx` does,
 * for the pending-invite sweep, which guarantees it happens before any file
 * browser renders.
 */
setWorkspaceKeyResolver(ensureWorkspaceKey);

// ── listing ─────────────────────────────────────────────────────────────────

/** Every workspace I am in, names decrypted. */
export async function listWorkspaces(): Promise<DecryptedWorkspace[]> {
  const { workspaces } = await api.workspaces.list();
  return Promise.all(workspaces.map(decryptSummary));
}

async function decryptSummary(w: WorkspaceSummaryWire): Promise<DecryptedWorkspace> {
  const base = {
    id: w.id,
    rootNodeId: w.rootNodeId,
    role: w.role,
    isOwner: w.isOwner,
    memberCount: w.memberCount,
    unread: w.unread,
    storage: w.storage,
    createdAt: w.createdAt
  };

  try {
    if (!w.nodeKeyWrapped) throw new Error('no key');
    // Listing hands over the envelope, so the whole page costs no extra
    // requests — and every row seeds the cache the file browser will want.
    const key =
      cachedWorkspaceKey(w.id) ?? (await adoptKey(w.id, w.rootNodeId, w.nodeKeyWrapped));
    return { ...base, name: await decryptName(key, w.nameEnc) };
  } catch {
    return { ...base, name: 'Could not be read', undecryptable: true };
  }
}

export interface WorkspaceView {
  workspace: DecryptedWorkspace;
  members: WorkspaceMemberWire[];
  key: CryptoKey;
  /** What this workspace is using. */
  storage: WorkspaceDetailWire['storage'];
  /** The pool it is charged against — its owner's drive. */
  pool: WorkspaceDetailWire['pool'];
  isOwner: boolean;
  lastReadAt: number;
}

/** Open a workspace: unwrap its key, decrypt its name, and hand back the member
 *  list the chat's @-autocomplete runs against. */
export async function enterWorkspace(workspaceId: string): Promise<WorkspaceView> {
  const detail = await api.workspaces.get(workspaceId);
  if (!detail.nodeKeyWrapped) {
    throw new Error('This workspace has not been shared with you yet.');
  }

  const key =
    cachedWorkspaceKey(workspaceId) ??
    (await adoptKey(workspaceId, detail.rootNodeId, detail.nodeKeyWrapped));

  let name = 'Could not be read';
  let undecryptable: true | undefined;
  try {
    name = detail.nameEnc ? await decryptName(key, detail.nameEnc) : 'Workspace';
  } catch {
    undecryptable = true;
  }

  const active = detail.members.filter((m) => m.state !== 'removed');

  return {
    workspace: {
      id: detail.id,
      rootNodeId: detail.rootNodeId,
      name,
      role: detail.role,
      isOwner: detail.isOwner,
      memberCount: active.filter((m) => m.state === 'active').length,
      unread: 0,
      storage: { usedBytes: detail.storage.usedBytes },
      createdAt: 0,
      ...(undecryptable ? { undecryptable } : {})
    },
    members: active,
    key,
    storage: detail.storage,
    pool: detail.pool,
    isOwner: detail.isOwner,
    lastReadAt: detail.lastReadAt
  };
}

// ── writes ──────────────────────────────────────────────────────────────────

/**
 * Create a workspace.
 *
 * The root key is generated here and wrapped to the creator's *own* public key
 * — not under their master key. That looks redundant for a workspace of one,
 * and it is the point: every member reaches the key by exactly one route, so
 * adding the second member is one more wrap rather than a change of scheme.
 */
export async function createWorkspace(name: string): Promise<DecryptedWorkspace> {
  const clean = assertValidName(name);
  // Fails fast on a locked vault rather than at the wrap, where the error would
  // arrive after the workspace name had already been typed.
  requireVault();

  const rootKey = await generateNodeKey();
  const me = await api.vault.get();

  const created = await api.workspaces.create({
    nameEnc: await encryptName(rootKey, clean),
    nodeKeyWrapped: await wrapKeyForRecipient(me.publicKey, rootKey)
  });

  workspaceKeys.set(created.id, rootKey);
  cacheNodeKey(created.rootNodeId, rootKey);

  return {
    id: created.id,
    rootNodeId: created.rootNodeId,
    name: clean,
    role: 'owner',
    isOwner: true,
    memberCount: 1,
    unread: 0,
    storage: { usedBytes: 0 },
    createdAt: Math.floor(Date.now() / 1000)
  };
}

/**
 * Invite someone.
 *
 * If they have a vault, their key is wrapped here and they are active
 * immediately. If they do not, the invite is recorded with no key and finished
 * by `completePendingWorkspaceKeys()` on a member's next unlock — you cannot
 * encrypt to a public key that does not exist yet.
 */
export async function inviteMember(
  workspaceId: string,
  email: string,
  role: WorkspaceRole
): Promise<{ pending: boolean }> {
  const key = await ensureWorkspaceKey(workspaceId);

  try {
    const recipient = await api.vault.publicKeyFor(email);
    return await api.workspaces.invite(workspaceId, {
      email,
      role,
      nodeKeyWrapped: await wrapKeyForRecipient(recipient.publicKey, key),
      fingerprint: recipient.fingerprint
    });
  } catch {
    // 404 — no vault yet. Record the invite; the wrap happens when they have
    // somewhere to wrap it to.
    return api.workspaces.invite(workspaceId, { email, role });
  }
}

/**
 * Finish workspace invitations that were waiting for a recipient to exist.
 *
 * The sibling of `completePendingShares()`, and it runs on the same trigger for
 * the same reason: the wrap needs the workspace key, which needs the vault,
 * which is only open in the browser. Best-effort — one failure must not stop
 * the rest, and the next unlock tries again.
 */
export async function completePendingWorkspaceKeys(): Promise<number> {
  requireVault();

  let pendingInvites: Awaited<ReturnType<typeof api.workspaces.pendingKeys>>['pending'];
  try {
    ({ pending: pendingInvites } = await api.workspaces.pendingKeys());
  } catch {
    return 0;
  }
  if (!pendingInvites.length) return 0;

  let completed = 0;
  for (const item of pendingInvites) {
    try {
      // The caller's own envelope comes back with the invite, so the workspace
      // key is one unwrap away without a second round trip.
      const key =
        cachedWorkspaceKey(item.workspaceId) ??
        (await unwrapKeyFromSender(requireVault().privateKey, item.myKeyWrapped));

      await api.workspaces.invite(item.workspaceId, {
        email: item.email,
        role: item.role,
        nodeKeyWrapped: await wrapKeyForRecipient(item.publicKey, key),
        fingerprint: item.fingerprint
      });
      completed++;
    } catch {
      // A workspace since deleted, a rate limit, a key that will not unwrap.
      // The invite stays pending until one attempt succeeds.
    }
  }

  return completed;
}

// ── chat ────────────────────────────────────────────────────────────────────

/**
 * Decrypt a page of messages.
 *
 * One that will not open comes back marked rather than dropped: a gap in a
 * conversation with no explanation is worse than a line saying it could not be
 * read, and dropping it silently would also hide a tampering attempt.
 */
export async function decryptMessages(
  workspaceKey: CryptoKey,
  messages: MessageWire[]
): Promise<DecryptedMessage[]> {
  const key = await chatKey(workspaceKey);

  return Promise.all(
    messages.map(async (m) => {
      const base = {
        id: m.id,
        authorId: m.authorId,
        authorName: m.authorName,
        mentions: m.mentions,
        createdAt: m.createdAt,
        editedAt: m.editedAt
      };
      try {
        return { ...base, body: await decryptMessage(key, m.bodyEnc) };
      } catch {
        return { ...base, body: 'This message could not be read.', undecryptable: true as const };
      }
    })
  );
}

/** Send a message. Mentions are resolved client-side against the member list —
 *  the server is never asked to interpret the text. */
export async function sendMessage(
  workspaceId: string,
  channel: Channel,
  text: string,
  members: WorkspaceMemberWire[]
): Promise<{ id: string; createdAt: number }> {
  const body = text.trim();
  if (!body) throw new Error('Nothing to send.');

  // `@name` becomes `<@id>` before encryption, so the recipient can draw a chip
  // instead of the characters the author typed. The ids handed to the server are
  // the same set either way — see encodeMentions.
  const encoded = encodeMentions(body, members);

  const key = await keyFor(channel);
  return api.workspaces.send(
    workspaceId,
    {
      bodyEnc: await encryptMessage(key, encoded.body, aadFor(channel)),
      mentions: encoded.mentions
    },
    channel.projectId,
    channel.channelId
  );
}

export async function editMessage(
  workspaceId: string,
  channel: Channel,
  messageId: string,
  text: string,
  members: WorkspaceMemberWire[]
): Promise<void> {
  const body = text.trim();
  if (!body) throw new Error('Nothing to send.');

  const encoded = encodeMentions(body, members);

  const key = await keyFor(channel);
  await api.workspaces.edit(workspaceId, messageId, {
    bodyEnc: await encryptMessage(key, encoded.body, aadFor(channel)),
    mentions: encoded.mentions
  });
}

/** Everything after a cursor, decrypted. The poll's whole job. */
/**
 * A chat channel: the workspace at large, or one project inside it.
 *
 * The key differs per channel and so does the AAD, so a message cannot be moved
 * between channels by rewriting a column — it simply fails to decrypt.
 */
export interface Channel {
  /** The project's id, for a project's own conversation. */
  projectId: string | null;
  /** A named channel's id, for one of the workspace's own channels. */
  channelId?: string | null;
  /** The workspace root's node key, or the project root's. */
  key: CryptoKey;
}

export const workspaceChannel = (key: CryptoKey): Channel => ({ projectId: null, key });
export const projectChannel = (projectId: string, key: CryptoKey): Channel => ({
  projectId,
  key
});
/** A named channel. Keyed under the **workspace** key, so everyone in the
 *  workspace can read it — see namedChannelChatKey. */
export const namedChannel = (channelId: string, workspaceKey: CryptoKey): Channel => ({
  projectId: null,
  channelId,
  key: workspaceKey
});

/** The message key for a channel — a different HKDF label for each. */
const keyFor = (ch: Channel) =>
  ch.projectId
    ? projectChatKey(ch.key)
    : ch.channelId
      ? namedChannelChatKey(ch.key, ch.channelId)
      : chatKey(ch.key);
const aadFor = (ch: Channel): ChannelKind =>
  ch.projectId ? 'project' : ch.channelId ? 'channel' : 'workspace';

export async function fetchMessages(
  workspaceId: string,
  channel: Channel,
  after?: string
): Promise<DecryptedMessage[]> {
  const { messages } = await api.workspaces.messages(workspaceId, after, channel.projectId, channel.channelId);
  const key = await keyFor(channel);
  return Promise.all(
    messages.map(async (m) => {
      try {
        return { ...m, body: await decryptMessage(key, m.bodyEnc, aadFor(channel)) };
      } catch {
        // One unreadable message must not blank the channel. Same reasoning as
        // `decryptNode`: it still exists, and saying so is more useful than
        // pretending the conversation has a hole in it.
        return { ...m, body: '', undecryptable: true } as DecryptedMessage;
      }
    })
  );
}

/** Whether a node lives in a workspace. The file browser uses it to decide
 *  which breadcrumb root and which storage figure to show. */
export const isWorkspaceNode = (node: { workspaceId: string | null }): boolean =>
  !!node.workspaceId;

